import React, { useState, useEffect, useContext } from 'react';
import { Table, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { AppContext } from '../application/provider';
import SueldoPlanillaService from '../axios_services/sueldoplanilla.service';
import UsuariosService from '../axios_services/usuarios.service';
import Loader from '../components/Loader/Loader';
import { ButtonUpdate, ButtonDelete, ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import TabsWithTable from '../components/Tabs/TabsWithTable';

const SueldosPlanilla = () => {
    const [state] = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [tabActiva, setTabActiva] = useState('vigente');
    
    // Datos cargados desde sueldo-planilla (Vigentes + Cesados con sueldo)
    const [allData, setAllData] = useState([]);
    const [registrosVigentes, setRegistrosVigentes] = useState([]);
    const [registrosCesados, setRegistrosCesados] = useState([]);
    
    // Para el modal - Usuarios sin sueldo
    const [usuariosCesadosSinSueldo, setUsuariosCesadosSinSueldo] = useState([]);
    
    const [filter, setFilter] = useState('');
    
    // Modales
    const [modalInsertOpen, setModalInsertOpen] = useState(false);
    const [modalUpdateOpen, setModalUpdateOpen] = useState(false);
    
    const [selectedPerfil, setSelectedPerfil] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [monto, setMonto] = useState('');
    const [estadoSeleccionado, setEstadoSeleccionado] = useState('vigente'); // 'vigente' o 'cesado'

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await SueldoPlanillaService.getSueldos();
            const all = res.data?.data || [];
            setAllData(all);
            
            // Filtrar registros para las tablas
            setRegistrosVigentes(all.filter(u => u.sueldo_planilla_id !== null && u.estado == 1));
            setRegistrosCesados(all.filter(u => u.sueldo_planilla_id !== null && u.estado == 0));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchCesadosParaModal = async () => {
        if (usuariosCesadosSinSueldo.length > 0) return;
        
        setLoading(true);
        try {
            const res = await UsuariosService.getUsuariosCesados();
            const users = res.usuarios || [];
            // Filtrar los que ya tienen sueldo asignado (buscando en allData)
            const sinSueldo = users.filter(u => 
                !allData.some(ad => ad.usuario_id === u.usuario_id && ad.sueldo_planilla_id !== null)
            );
            setUsuariosCesadosSinSueldo(sinSueldo);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const toggleInsertModal = () => {
        setSelectedPerfil('');
        setSelectedUser(null);
        setMonto('');
        setEstadoSeleccionado('vigente');
        setModalInsertOpen(!modalInsertOpen);
    };

    const toggleUpdateModal = (user = null) => {
        if (user) {
            setSelectedUser(user);
            setMonto(user.monto_sueldo || '');
        }
        setModalUpdateOpen(!modalUpdateOpen);
    };

    const handleSave = async () => {
        if (!selectedUser || !monto || isNaN(monto)) {
            alert('Seleccione un colaborador e ingrese un monto válido');
            return;
        }

        setLoading(true);
        try {
            await SueldoPlanillaService.storeSueldo({
                usuario_id: selectedUser.usuario_id,
                monto_sueldo: parseFloat(monto)
            });
            setModalInsertOpen(false);
            setModalUpdateOpen(false);
            loadData();
            // Resetear cesados sin sueldo para que se vuelvan a cargar si es necesario
            setUsuariosCesadosSinSueldo([]);
        } catch (e) {
            alert('Error al guardar sueldo');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este sueldo?')) return;
        setLoading(true);
        try {
            await SueldoPlanillaService.deleteSueldo(id);
            loadData();
            setUsuariosCesadosSinSueldo([]);
        } catch (e) {
            alert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (v) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(v);

    const renderTable = (data) => {
        const filtered = data.filter(r => 
            r.nombre_completo.toLowerCase().includes(filter.toLowerCase()) ||
            r.perfil.toLowerCase().includes(filter.toLowerCase())
        );

        return (
            <div className="table-container">
                <Table hover responsive striped>
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: '20px' }}>COLABORADOR</th>
                            <th>PERFIL / CARGO</th>
                            <th className="text-center">SUELDO MENSUAL</th>
                            <th className="text-center">ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center py-4">No hay sueldos configurados</td>
                            </tr>
                        ) : filtered.map(u => (
                            <tr key={u.usuario_id}>
                                <td style={{ paddingLeft: '20px' }}>{u.nombre_completo}</td>
                                <td>{u.perfil}</td>
                                <td className="text-center">
                                    {fmt(u.monto_sueldo)}
                                </td>
                                <td className="text-center">
                                    <div className="acciones-container justify-center">
                                        <ButtonUpdate onClick={() => toggleUpdateModal(u)} />
                                        <ButtonDelete onClick={() => handleDelete(u.sueldo_planilla_id)} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    const tabs = [
        {
            key: 'vigente',
            label: 'Vigente',
            content: renderTable(registrosVigentes)
        },
        {
            key: 'cesado',
            label: 'Cesados',
            content: renderTable(registrosCesados)
        }
    ];

    const userHasSalary = selectedUser && allData.some(ad => String(ad.usuario_id) === String(selectedUser.usuario_id) && ad.sueldo_planilla_id !== null);

    // Listas para el modal de inserción
    const allProfiles = state.catalogos?.perfiles || [];
    
    const usersByProfile = selectedPerfil
        ? (estadoSeleccionado === 'vigente' 
            ? (state.user?.personal || []) 
            : usuariosCesadosSinSueldo
          ).filter(u => {
            const isMatch = String(u.perfil_id) === String(selectedPerfil);
            return isMatch;
          })
        : [];

    return (
        <div className="caja-flujo-container rh-administracion-scope">
            {loading && <Loader />}
            
            <div className="flex items-center justify-between mb-4">
                <h1>GESTIÓN DE SUELDOS PLANILLA</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Buscar Colaborador:</label>
                    <input
                        className="form-control"
                        type="text"
                        placeholder="Nombre o Perfil..."
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />
                </div>
            </div>

            <ButtonSearch onClick={() => {}} />

            <TabsWithTable
                tabs={tabs}
                defaultTab="vigente"
                onTabChange={setTabActiva}
                actionButton={
                    <div className="nuevo-movimiento-section">
                        <ButtonInsert onClick={toggleInsertModal} />
                    </div>
                }
            />

            {/* MODAL INSERTAR - Con Perfil y Colaborador */}
            <Modal isOpen={modalInsertOpen} toggle={toggleInsertModal} centered className="custom-modal">
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>NUEVO SUELDO PERSONAL</h1>
                        <button className="close-btn" onClick={toggleInsertModal}>X</button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form style={{ padding: '20px' }} onSubmit={e => { e.preventDefault(); handleSave(); }}>
                            <FormGroup className="mb-4">
                                <Label>Estado:</Label>
                                <Input 
                                    type="select" 
                                    className="form-control" 
                                    value={estadoSeleccionado}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setEstadoSeleccionado(val);
                                        setSelectedUser(null);
                                        if (val === 'cesado') {
                                            fetchCesadosParaModal();
                                        }
                                    }}
                                >
                                    <option value="vigente">Vigente</option>
                                    <option value="cesado">Cesado</option>
                                </Input>
                            </FormGroup>

                            <FormGroup className="mb-4">
                                <Label>Perfil:</Label>
                                <Input 
                                    type="select" 
                                    className="form-control" 
                                    value={selectedPerfil} 
                                    onChange={(e) => {
                                        setSelectedPerfil(e.target.value);
                                        setSelectedUser(null);
                                    }}
                                >
                                    <option value="">Seleccione un Perfil</option>
                                    {allProfiles.map(p => <option key={p.perfil_id} value={p.perfil_id}>{p.descripcion}</option>)}
                                </Input>
                            </FormGroup>

                            <FormGroup className="mb-4">
                                <Label>Colaborador:</Label>
                                <Input 
                                    type="select" 
                                    className="form-control" 
                                    disabled={!selectedPerfil}
                                    value={selectedUser?.usuario_id || ''}
                                    onChange={(e) => {
                                        const user = usersByProfile.find(u => String(u.usuario_id) === e.target.value);
                                        setSelectedUser(user);
                                    }}
                                >
                                    <option value="">Seleccione un Colaborador</option>
                                    {usersByProfile.map(u => (
                                        <option key={u.usuario_id} value={u.usuario_id}>
                                            {u.nombre_completo_usuario || u.nombre_completo || (u.apellidos + ', ' + u.nombre)}
                                        </option>
                                    ))}
                                </Input>
                                {userHasSalary && (
                                    <div className="text-danger mt-2" style={{ fontSize: '13px' }}>
                                        ⚠️ Este colaborador ya tiene un sueldo registrado
                                    </div>
                                )}
                            </FormGroup>

                            <FormGroup className="mb-4">
                                <Label>Monto Sueldo (S/):</Label>
                                <Input 
                                    type="number" 
                                    placeholder="0.00" 
                                    value={monto} 
                                    onChange={e => setMonto(e.target.value)}
                                />
                            </FormGroup>

                            <div className="button-container mt-2">
                                <Button className="button-custom" onClick={handleSave} disabled={userHasSalary}>
                                    REGISTRAR
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            {/* MODAL ACTUALIZAR */}
            <Modal isOpen={modalUpdateOpen} toggle={() => setModalUpdateOpen(false)} centered className="custom-modal">
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>ACTUALIZAR SUELDO</h1>
                        <button className="close-btn" onClick={() => setModalUpdateOpen(false)}>X</button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form style={{ padding: '20px' }} onSubmit={e => { e.preventDefault(); handleSave(); }}>
                            <FormGroup className="mb-4">
                                <Label>Colaborador:</Label>
                                <Input value={selectedUser?.nombre_completo || ''} disabled className="bg-light" />
                            </FormGroup>
                            <FormGroup className="mb-4">
                                <Label>Monto Sueldo (S/):</Label>
                                <Input 
                                    type="number" 
                                    placeholder="0.00" 
                                    value={monto} 
                                    onChange={e => setMonto(e.target.value)}
                                    autoFocus
                                />
                            </FormGroup>

                            <div className="button-container mt-2">
                                <Button className="button-custom" onClick={handleSave}>
                                    ACTUALIZAR
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>
        </div>
    );
};

export default SueldosPlanilla;
